/**
 * The global period (docs/07-gui-proposal §2): ONE choice at the top of the screen that every
 * page reads, and that lives on the URL so a link opens the same screen for someone else.
 *
 * Pure on purpose (S1): "today" is passed in, never read from the clock — that is what makes
 * every preset measurable in a test, and what keeps the period the same for a whole render.
 */
export const PRESETS = [
  'today',
  'last7',
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'thisYear',
  'yearToDate',
  'custom',
] as const;

export type Preset = (typeof PRESETS)[number];

export interface Period {
  readonly preset: Preset;
  readonly from: string;
  readonly to: string;
}

export const PRESET_LABELS: Readonly<Record<Preset, string>> = {
  today: 'Hôm nay',
  last7: '7 ngày',
  thisMonth: 'Tháng này',
  lastMonth: 'Tháng trước',
  thisQuarter: 'Quý này',
  thisYear: 'Năm nay',
  yearToDate: 'Lũy kế năm',
  custom: 'Tuỳ chọn',
};

export const DEFAULT_PRESET: Exclude<Preset, 'custom'> = 'thisMonth';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** ISO date arithmetic through UTC: the business day is a label, never an instant. */
function shiftDays(date: string, days: number): string {
  const time = Date.parse(`${date}T00:00:00Z`) + days * 86_400_000;
  return new Date(time).toISOString().slice(0, 10);
}

function firstOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

function lastOfMonth(date: string): string {
  const [year, month] = [Number(date.slice(0, 4)), Number(date.slice(5, 7))];
  const nextMonthStart =
    month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`;
  return shiftDays(nextMonthStart, -1);
}

export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) return false;
  const time = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value;
}

export function isPreset(value: unknown): value is Preset {
  return typeof value === 'string' && (PRESETS as readonly string[]).includes(value);
}

/**
 * The range a preset covers on a given day. `custom` has no range of its own — the caller keeps
 * the two dates the reader picked.
 */
export function rangeOf(
  preset: Exclude<Preset, 'custom'>,
  today: string,
): { from: string; to: string } {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'last7':
      return { from: shiftDays(today, -6), to: today };
    case 'thisMonth':
      return { from: firstOfMonth(today), to: lastOfMonth(today) };
    case 'lastMonth': {
      const lastMonthDay = shiftDays(firstOfMonth(today), -1);
      return { from: firstOfMonth(lastMonthDay), to: lastMonthDay };
    }
    case 'thisQuarter': {
      const year = today.slice(0, 4);
      const quarter = Math.floor((Number(today.slice(5, 7)) - 1) / 3);
      const startMonth = String(quarter * 3 + 1).padStart(2, '0');
      const endMonth = String(quarter * 3 + 3).padStart(2, '0');
      return { from: `${year}-${startMonth}-01`, to: lastOfMonth(`${year}-${endMonth}-01`) };
    }
    case 'thisYear':
      return { from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` };
    case 'yearToDate':
      /* Lũy kế từ 01/01 tới HÔM NAY (chủ dự án 17/09/2026) — "Năm nay" vẫn là trọn T1–T12. */
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
  }
}

/**
 * Read the period from the URL. Anything missing or unreadable falls back to the default preset
 * — a broken link opens a working screen, it does not open an error.
 */
export function readPeriod(params: URLSearchParams, today: string): Period {
  const preset = params.get('preset');
  const from = params.get('from');
  const to = params.get('to');
  if (preset === 'custom' && isIsoDate(from) && isIsoDate(to) && from <= to)
    return { preset, from, to };
  const known: Exclude<Preset, 'custom'> =
    isPreset(preset) && preset !== 'custom' ? preset : DEFAULT_PRESET;
  return { preset: known, ...rangeOf(known, today) };
}

/** What the period writes back onto the URL. */
export function periodParams(period: Period): Record<string, string> {
  return period.preset === 'custom'
    ? { preset: 'custom', from: period.from, to: period.to }
    : { preset: period.preset };
}

/** Move a period to another preset, keeping the custom dates when there is nothing to compute. */
export function withPreset(period: Period, preset: Preset, today: string): Period {
  if (preset === 'custom') return { ...period, preset };
  return { preset, ...rangeOf(preset, today) };
}

/**
 * Năm xa nhất kho dữ liệu có (backfill 01/01/2021, docs/08 §2) và trần trên của một ngày gõ tay.
 * Không phải để giới hạn người dùng cho vui: ô `<input type="date">` phát sự kiện đổi sau MỖI chữ
 * số của phần năm, nên gõ "2026" đi qua các giá trị 0002 · 0020 · 0202 — cả ba đều là ngày ISO hợp
 * lệ. Nhận bừa một trong ba là ghi lên URL một kỳ vô nghĩa, và kỳ ấy rơi về mặc định ⇒ **ô năm gõ
 * không được** (chủ dự án báo 13/09/2026).
 */
export const FIRST_DATA_YEAR = 2021;
const LAST_USABLE_YEAR = 2100;

/** Một ngày người dùng gõ xong, chứ không phải một ngày đang gõ dở. */
export function isUsableDate(value: unknown): value is string {
  if (!isIsoDate(value)) return false;
  const year = Number(value.slice(0, 4));
  return year >= 1900 && year <= LAST_USABLE_YEAR;
}

/** Hai đầu kỳ do người đọc gõ, xếp lại cho đúng chiều — gõ ngược thì đảo, không báo lỗi. */
export function orderedRange(a: string, b: string): { from: string; to: string } {
  return a <= b ? { from: a, to: b } : { from: b, to: a };
}

/** Cả tháng `YYYY-MM` thành một kỳ. Chuỗi rác ⇒ `null`, người gọi không đổi gì cả. */
export function monthRange(month: unknown): { from: string; to: string } | null {
  if (typeof month !== 'string' || !/^\d{4}-\d{2}$/.test(month)) return null;
  const from = `${month}-01`;
  if (!isUsableDate(from)) return null;
  return { from, to: lastOfMonth(from) };
}

/** Cả năm `YYYY` thành một kỳ. */
export function yearRange(year: unknown): { from: string; to: string } | null {
  if (typeof year !== 'string' || !/^\d{4}$/.test(year)) return null;
  const from = `${year}-01-01`;
  if (!isUsableDate(from)) return null;
  return { from, to: `${year}-12-31` };
}

/**
 * `YYYY-MM` để đổ vào ô chọn tháng — CHỈ khi kỳ đang xem đúng bằng trọn một tháng. Kỳ 01/04 →
 * 31/08 mà vẫn sáng đèn "tháng 4" là nói sai về thứ đang hiện trên màn.
 */
export function monthValue(period: Period): string {
  const month = period.from.slice(0, 7);
  return period.from === `${month}-01` && period.to === lastOfMonth(period.from) ? month : '';
}

/** Cùng luật ấy cho ô chọn năm. */
export function yearValue(period: Period): string {
  const year = period.from.slice(0, 4);
  return period.from === `${year}-01-01` && period.to === `${year}-12-31` ? year : '';
}

/** Các năm chọn được: từ năm gần nhất lùi về năm đầu tiên có dữ liệu. */
export function yearOptions(today: string): string[] {
  const years: string[] = [];
  for (let year = Number(today.slice(0, 4)); year >= FIRST_DATA_YEAR; year -= 1) {
    years.push(String(year));
  }
  return years;
}

/**
 * The slice of a period that has actually happened yet.
 *
 * 🚨 "Tháng này" runs to the 30th, but on the 12th only twelve days carry data — comparing that
 * against a WHOLE previous month draws a −60 % arrow that means nothing. Every block that
 * compares two periods measures this slice instead, and says so on screen.
 */
export function elapsed(
  period: Period,
  today: string,
): { from: string; to: string; clamped: boolean } {
  if (period.to <= today) return { from: period.from, to: period.to, clamped: false };
  if (period.from > today) return { from: period.from, to: period.from, clamped: true };
  return { from: period.from, to: today, clamped: true };
}
